import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Table, Typography, theme } from 'antd';
import { PlusOutlined, CheckOutlined } from '@ant-design/icons';
import type { ContactResponse, ContactSortField } from '@/types/api';
import TableContainer from '@/components/common/TableContainer';
import { ContactAddressDisplay } from '@/components/common/ContactAddressDisplay';
import { formatPhoneFromApiForDisplay, formatMobileFromApiForDisplay } from '@/utils/phoneUtils';
import { controlHeightTokens } from '@/styles/tokens';

const { Text } = Typography;

/**
 * #91 — the vendor detail contact section is VIEW-ONLY.
 * Contacts are created and edited exclusively in the Contacts module;
 * each row links to that contact's page there (`/auth/contacts/:id`).
 */
interface ContactsSectionProps {
  contacts: ContactResponse[];
  loading: boolean;
  includeInactive: boolean;
  onIncludeInactiveChange: (checked: boolean) => void;
  /** Navigate to the contact's page in the Contacts module. */
  onOpenContact: (contact: ContactResponse) => void;
  /** Navigate to the Contacts module (create surface lives there). */
  onAddInContactsModule: () => void;
}

const ContactsSection = ({
  contacts,
  loading,
  includeInactive,
  onIncludeInactiveChange,
  onOpenContact,
  onAddInContactsModule,
}: ContactsSectionProps) => {
  const { t } = useTranslation('vendors');
  const { token } = theme.useToken();

  // Sorting state for Contact table
  const [contactSortBy, setContactSortBy] = useState<ContactSortField | undefined>(undefined);
  const [contactSortOrder, setContactSortOrder] = useState<0 | 1 | undefined>(undefined);

  return (
    <div className="tw-mt-8">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h3
          style={{
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
            margin: 0,
          }}
        >
          {t('vendor.sections.contacts')}
        </h3>
        <div className="tw-flex tw-items-center tw-gap-4">
          <Checkbox
            checked={includeInactive}
            onChange={(e) => onIncludeInactiveChange(e.target.checked)}
          >
            {t('vendor.labels.includeInactiveContacts')}
          </Checkbox>
          {/* #91 方式2 — the add button LINKS to the Contacts module */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
            onClick={onAddInContactsModule}
          >
            {t('vendor.contactsPolicy.addContact')}
          </Button>
        </div>
      </div>
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('vendor.contactsPolicy.managedInModule')}
      </Text>
      <TableContainer>
        <Table<ContactResponse>
          dataSource={
            contacts
              ?.filter((c) => includeInactive || c.isActive)
              ?.slice()
              ?.sort((a, b) => {
                // Apply sorting based on current sort state
                if (contactSortBy === undefined) {
                  // Default sort: Primary first, then by contactId descending (newest first)
                  if (a.isPrimary !== b.isPrimary) {
                    return a.isPrimary ? -1 : 1;
                  }
                  return b.contactId - a.contactId;
                }

                // Client-side sorting based on selected column
                const direction = contactSortOrder === 0 ? 1 : -1; // 0 = ascend, 1 = descend

                switch (contactSortBy) {
                  case 2: // name
                    return direction * (a.name || '').localeCompare(b.name || '');
                  case 3: // jobTitle
                    return direction * (a.jobTitle || '').localeCompare(b.jobTitle || '');
                  case 4: // email
                    return direction * (a.email || '').localeCompare(b.email || '');
                  case 5: // phone
                    return direction * (a.phoneNumber || '').localeCompare(b.phoneNumber || '');
                  case 6: // mobile
                    return direction * (a.mobileNumber || '').localeCompare(b.mobileNumber || '');
                  case 12: // address
                    return direction * (a.address || '').localeCompare(b.address || '');
                  default:
                    return 0;
                }
              }) || []
          }
          rowKey="contactId"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          onRow={(record) => ({
            onClick: () => onOpenContact(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            pageSize: 10,
          }}
          scroll={{ x: 'max-content' }}
          loading={loading}
          onChange={(_pagination, _filters, sorter) => {
            // Handle sorting - map column key to API sortBy field
            const sortInfo = Array.isArray(sorter) ? sorter[0] : sorter;
            if (sortInfo?.columnKey && sortInfo?.order) {
              const columnToSortBy: Record<string, ContactSortField> = {
                name: 2,
                jobTitle: 3,
                email: 4,
                phone: 5,
                mobile: 6,
                isActive: 7,
                address: 12,
              };
              const sortBy = columnToSortBy[sortInfo.columnKey as string];
              if (sortBy !== undefined) {
                setContactSortBy(sortBy);
                setContactSortOrder(sortInfo.order === 'ascend' ? 0 : 1);
              }
            } else {
              // Clear sorting when user clears the sort
              setContactSortBy(undefined);
              setContactSortOrder(undefined);
            }
          }}
          columns={[
            {
              title: t('vendor.table.primary'),
              dataIndex: 'isPrimary',
              key: 'isPrimary',
              width: 120,
              align: 'center',
              render: (isPrimary: boolean) =>
                isPrimary ? (
                  <CheckOutlined style={{ color: token.colorPrimary, fontSize: token.sizeMD }} />
                ) : null,
            },
            {
              title: t('vendor.table.name'),
              dataIndex: 'name',
              key: 'name',
              sorter: true,
              sortOrder:
                contactSortBy === 2 ? (contactSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
              // #91 — clickable name links to the contact's page in the Contacts module
              render: (name: string, record: ContactResponse) => (
                <a
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenContact(record);
                  }}
                  style={{ color: token.colorPrimary, cursor: 'pointer' }}
                >
                  {name}
                </a>
              ),
            },
            {
              title: t('vendor.table.jobTitle'),
              dataIndex: 'jobTitle',
              key: 'jobTitle',
              sorter: true,
              sortOrder:
                contactSortBy === 3 ? (contactSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
              render: (title: string | null) => title || '-',
            },
            {
              title: t('vendor.table.phone'),
              key: 'phone',
              sorter: true,
              sortOrder:
                contactSortBy === 5 ? (contactSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
              render: (_: unknown, record: ContactResponse) => {
                return (
                  formatPhoneFromApiForDisplay(
                    {
                      phoneCountryCode: record.phoneCountryCode,
                      phoneNumber: record.phoneNumber,
                      phoneExtension: record.phoneExtension,
                    },
                    t('vendor.fields.ext')
                  ) || '-'
                );
              },
            },
            {
              title: t('vendor.table.mobile'),
              key: 'mobile',
              sorter: true,
              sortOrder:
                contactSortBy === 6 ? (contactSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
              render: (_: unknown, record: ContactResponse) => {
                return (
                  formatMobileFromApiForDisplay({
                    mobileCountryCode: record.mobileCountryCode,
                    mobileNumber: record.mobileNumber,
                  }) || '-'
                );
              },
            },
            {
              title: t('vendor.table.fax'),
              dataIndex: 'fax',
              key: 'fax',
              showSorterTooltip: false,
              render: (text: string | null) => text || '-',
            },
            {
              title: t('vendor.table.email'),
              dataIndex: 'email',
              key: 'email',
              sorter: true,
              sortOrder:
                contactSortBy === 4 ? (contactSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
              render: (email: string) => <a href={`mailto:${email}`}>{email}</a>,
            },
            {
              title: t('vendor.table.address'),
              dataIndex: 'address',
              key: 'address',
              width: 300,
              ellipsis: true,
              sorter: true,
              sortOrder:
                contactSortBy === 12 ? (contactSortOrder === 0 ? 'ascend' : 'descend') : undefined,
              showSorterTooltip: false,
              render: (_: unknown, record: ContactResponse) => (
                <ContactAddressDisplay contact={record} />
              ),
            },
          ]}
        />
      </TableContainer>
    </div>
  );
};

export default ContactsSection;
